import { useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Days after signup to send each email (0 = immediately on signup)
// Note: email 0 (welcome) is sent in Auth.tsx on signup, so we start from 1
const EMAIL_SCHEDULE = [0, 3, 7, 14, 21, 28, 35, 42, 49, 56, 63, 70];

export function useEmailNurturing() {
  const { user } = useAuth();
  const hasCheckedRef = useRef(false);
  const isCheckingRef = useRef(false);

  const checkAndSendNurturingEmail = useCallback(async () => {
    if (!user?.id || !user?.email) return;
    
    // Prevent concurrent calls
    if (isCheckingRef.current) {
      console.log("[NURTURING] Already checking, skipping...");
      return;
    }
    
    isCheckingRef.current = true;

    try {
      // Get user creation date from profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("created_at")
        .eq("user_id", user.id)
        .single();

      if (!profile?.created_at) {
        isCheckingRef.current = false;
        return;
      }

      const createdAt = new Date(profile.created_at);
      const now = new Date();
      const daysSinceSignup = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));

      // Get all emails already sent for this user from the database
      const { data: sentEmails, error: fetchError } = await supabase
        .from("nurturing_emails_sent")
        .select("sequence_number")
        .eq("user_id", user.id)
        .eq("email_type", "nurturing");

      if (fetchError) {
        console.error("[NURTURING] Error fetching sent emails:", fetchError);
        isCheckingRef.current = false;
        return;
      }

      const sentSequences = new Set((sentEmails || []).map(e => e.sequence_number));

      // Find the next email to send (skip sequence 0 as it's handled by welcome email)
      for (let i = 1; i < EMAIL_SCHEDULE.length; i++) {
        // Skip if already sent
        if (sentSequences.has(i)) continue;

        // Check if enough days have passed
        if (daysSinceSignup >= EMAIL_SCHEDULE[i]) {
          console.log(`[NURTURING] Attempting to send email ${i} to ${user.email}`);
          
          // First, try to insert the record to claim the send (idempotency)
          const { error: insertError } = await supabase
            .from("nurturing_emails_sent")
            .insert({
              user_id: user.id,
              sequence_number: i,
              email_type: "nurturing",
            });

          // If insert fails due to unique constraint, email was already sent
          if (insertError) {
            if (insertError.code === "23505") {
              console.log(`[NURTURING] Email ${i} already sent (race condition prevented)`);
              continue;
            }
            console.error(`[NURTURING] Error recording email ${i}:`, insertError);
            continue;
          }

          // Record inserted successfully, now send the email
          const { error: sendError } = await supabase.functions.invoke("send-nurturing-email", {
            body: {
              email: user.email,
              sequenceNumber: i,
              userId: user.id,
              origin: window.location.origin,
            },
          });

          if (sendError) {
            console.error(`[NURTURING] Failed to send email ${i}:`, sendError);
            // Don't delete the record - we'll retry on next check
          } else {
            console.log(`[NURTURING] Email ${i} sent successfully`);
          }

          // Only send one email per check
          break;
        }
      }
    } catch (error) {
      console.error("[NURTURING] Error in checkAndSendNurturingEmail:", error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [user?.id, user?.email]);

  useEffect(() => {
    // Only check once per mount to avoid multiple sends
    if (hasCheckedRef.current) return;
    
    // Check with a delay after mount
    const timer = setTimeout(() => {
      hasCheckedRef.current = true;
      checkAndSendNurturingEmail();
    }, 10000); // 10 second delay to ensure auth is stable

    return () => {
      clearTimeout(timer);
    };
  }, [checkAndSendNurturingEmail]);
}
